import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid, FormControlLabel, Checkbox,
  IconButton, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Autocomplete, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useListaPrecioStore } from '../store/listaPrecioStore';
import { http } from '../../../../api/http';
import type { ListaPrecio, ListaPrecioDetalle } from '../types/listaPrecio.types';

interface ArticuloOption {
  idArticulo: number;
  descripcion: string;
  codigoArticulo?: string;
}

export const ListaPrecioFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'nueva';
  const { selectedLista, loading, fetchListaById, saveLista } = useListaPrecioStore();

  const [formData, setFormData] = useState<Partial<ListaPrecio>>({
    nombre: '', descripcion: '', activo: true, detalles: [],
  });
  const [articulos, setArticulos] = useState<ArticuloOption[]>([]);
  const [articulosLoading, setArticulosLoading] = useState(false);

  useEffect(() => { if (isEdit && id) fetchListaById(parseInt(id)); }, [id]);
  useEffect(() => { if (isEdit && selectedLista) setFormData(selectedLista); }, [selectedLista]);

  useEffect(() => {
    setArticulosLoading(true);
    http.get('/articulo/list')
      .then((res) => setArticulos(res.data))
      .catch(() => setArticulos([]))
      .finally(() => setArticulosLoading(false));
  }, []);

  const handleChange = (field: string, value: any) => setFormData((prev) => ({ ...prev, [field]: value }));

  const addDetalle = () => {
    setFormData((prev) => ({
      ...prev,
      detalles: [...(prev.detalles || []), { idListaPrecioDetalle: 0, idListaPrecio: 0, idArticulo: null, precioVenta: 0, precioMinimo: null, porcentajeDescuentoMax: null, precioCosto: null, margen: null, activo: true, articulo: null } as ListaPrecioDetalle],
    }));
  };

  const handleDetalleChange = (index: number, field: string, value: any) => {
    setFormData((prev) => {
      const detalles = [...(prev.detalles || [])];
      detalles[index] = { ...detalles[index], [field]: value };
      return { ...prev, detalles };
    });
  };

  const removeDetalle = (index: number) => {
    setFormData((prev) => ({ ...prev, detalles: (prev.detalles || []).filter((_, i) => i !== index) }));
  };

  const handleSubmit = async () => {
    try {
      const cleanDetalles = (formData.detalles || [])
        .filter((d) => d.idArticulo != null)
        .map(({ articulo, ...rest }) => ({
          ...rest,
          idArticulo: rest.idArticulo as number,
          activo: rest.activo ?? true,
        }));
      const payload: ListaPrecio = {
        idListaPrecio: formData.idListaPrecio || 0,
        nombre: formData.nombre || '',
        descripcion: formData.descripcion || null,
        idMoneda: formData.idMoneda || null,
        fechaInicio: formData.fechaInicio || null,
        fechaFin: formData.fechaFin || null,
        activo: formData.activo ?? true,
        moneda: null,
        detalles: cleanDetalles as ListaPrecioDetalle[],
      };
      await saveLista(payload);
      navigate('/ventas/lista-precios');
    } catch { /* handled */ }
  };

  const getArticuloOption = (idArticulo: number | null) => {
    if (!idArticulo) return null;
    return articulos.find((a) => a.idArticulo === idArticulo) || null;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/ventas/lista-precios')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" fontWeight="bold">{isEdit ? 'Editar Lista de Precio' : 'Nueva Lista de Precio'}</Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Datos Generales</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}><TextField label="Nombre" size="small" fullWidth value={formData.nombre || ''} onChange={(e) => handleChange('nombre', e.target.value)} required /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}><TextField label="Descripcion" size="small" fullWidth value={formData.descripcion || ''} onChange={(e) => handleChange('descripcion', e.target.value)} /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}><TextField label="Fecha Inicio" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }} value={formData.fechaInicio || ''} onChange={(e) => handleChange('fechaInicio', e.target.value)} /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}><TextField label="Fecha Fin" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }} value={formData.fechaFin || ''} onChange={(e) => handleChange('fechaFin', e.target.value)} /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}><FormControlLabel control={<Checkbox checked={formData.activo ?? true} onChange={(e) => handleChange('activo', e.target.checked)} />} label="Activo" /></Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Articulos y Precios</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addDetalle} size="small">Agregar Articulo</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={50}>#</TableCell>
                <TableCell>Articulo</TableCell>
                <TableCell width={150}>Precio Venta</TableCell>
                <TableCell width={150}>Precio Minimo</TableCell>
                <TableCell width={150}>Desc. Maximo %</TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(formData.detalles || []).map((det, idx) => (
                <TableRow key={idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={articulos}
                      loading={articulosLoading}
                      value={getArticuloOption(det.idArticulo)}
                      onChange={(_, newValue) => handleDetalleChange(idx, 'idArticulo', newValue?.idArticulo || null)}
                      getOptionLabel={(option) => `${option.codigoArticulo || ''} - ${option.descripcion}`}
                      isOptionEqualToValue={(option, value) => option.idArticulo === value.idArticulo}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Buscar articulo..."
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {articulosLoading ? <CircularProgress size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.idArticulo}>
                          <Box>
                            <Typography variant="body2">{option.descripcion}</Typography>
                            {option.codigoArticulo && (
                              <Typography variant="caption" color="text.secondary">{option.codigoArticulo}</Typography>
                            )}
                          </Box>
                        </li>
                      )}
                    />
                  </TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.precioVenta || 0} onChange={(e) => handleDetalleChange(idx, 'precioVenta', parseFloat(e.target.value) || 0)} /></TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.precioMinimo || ''} onChange={(e) => handleDetalleChange(idx, 'precioMinimo', parseFloat(e.target.value) || null)} /></TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.porcentajeDescuentoMax || ''} onChange={(e) => handleDetalleChange(idx, 'porcentajeDescuentoMax', parseFloat(e.target.value) || null)} /></TableCell>
                  <TableCell><IconButton size="small" color="error" onClick={() => removeDetalle(idx)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
              {(formData.detalles || []).length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin articulos en la lista</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={() => navigate('/ventas/lista-precios')}>Cancelar</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={loading}>{loading ? 'Guardando...' : 'Guardar'}</Button>
      </Box>
    </Box>
  );
};
